import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsis,
    faPenToSquare,
    faCircleMinus,
    faArrowDownShortWide,
    faArrowDownWideShort,
    faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { getChargingConnectorByChargingPoint, deleteChargingConnector } from "../../api/ChargingConnector";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";

import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import ConnectorStatusBadge from "../../components/connector/ConnectorStatusBadge";


const PointConnectors = ({pointId}) => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const [connectors, setConnectors] = useState([]);
    const [filteredConnectors, setFilteredConnectors] = useState([]);
    const [sortColumn, setSortColumn] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [search] = useState("");
    const navigate = useNavigate();
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: "",
        confirm: () => { },
    });

    useEffect(() => {
        handleGetConnectors();
        const intervalId = setInterval(handleGetConnectors, 10000);
        return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);

    useEffect(() => {
        const applyFilterAndSort = () => {
            let filtered = connectors;

            if (search) {
                const searchLower = search.toLowerCase();
                filtered = connectors.filter(
                    (connector) =>
                        connector.chargingConnectorPublicId?.toLowerCase().includes(searchLower) ||
                        connector.name?.toLowerCase().includes(searchLower) ||
                        connector.chargingConnectorType?.toLowerCase().includes(searchLower) ||
                        connector.chargingConnectorStatus?.toLowerCase().includes(searchLower)
                );
            }

            const sorted = [...filtered].sort((a, b) => {
                const aValue = a[sortColumn]?.toString().toLowerCase();
                const bValue = b[sortColumn]?.toString().toLowerCase();
                return sortOrder === "asc" ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
            });

            setFilteredConnectors(sorted);
        };

        applyFilterAndSort();
    }, [search, connectors, sortColumn, sortOrder]);

    const handleGetConnectors = async () => {
        try {
            const accessToken = await getAccessToken();
            const newConnectors = await getChargingConnectorByChargingPoint(pointId, accessToken);
            setConnectors(newConnectors);
            setFilteredConnectors(newConnectors);
        } catch (error) {
            toast.error(error?.message);
        }
    };

    const handleDeleteConnector = async (chargingConnectorPublicId) => {
        const toastId = toast.loading(t("toast.deleting", { entity: t("entity.connector") }));
        try {
            const accessToken = await getAccessToken();
            await deleteChargingConnector(chargingConnectorPublicId, accessToken);
            const newConnectors = await getChargingConnectorByChargingPoint(pointId, accessToken);
            setConnectors(newConnectors);
            toast.success(t("toast.deleted", { entity: t("entity.connector") }), { id: toastId });
        } catch (error) {
            toast.error(error?.message, { id: toastId });
        }
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn === column) {
            return sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort;
        }
        return null;
    };

    const goToConnector = (chargingConnectorPublicId) => {
        navigate(`/connector/${chargingConnectorPublicId}`, {
            state: {
                chargingPointPublicId: pointId,
            },
        });
    };

console.log("Filtered Connectors:", filteredConnectors); // Debugging line to check filtered connectors

    return (
        <>
            <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                <table className="table-default w-full">
                    <thead>
                        <tr>
                            <th
                                className="text-left cursor-pointer"
                                onClick={() => handleSort("chargingConnectorPublicId")}
                            >
                                {t("table.connectorId")}
                                {getSortIcon("chargingConnectorPublicId") && (
                                    <FontAwesomeIcon
                                        icon={getSortIcon("chargingConnectorPublicId")}
                                        className="w-4 pl-2"
                                    />
                                )}
                            </th>
                            <th
                                className="text-left cursor-pointer"
                                onClick={() => handleSort("chargingConnectorName")}
                            >
                                {t("common.name")}
                                {getSortIcon("chargingConnectorName") && (
                                    <FontAwesomeIcon
                                        icon={getSortIcon("chargingConnectorName")}
                                        className="w-4 pl-2"
                                    />
                                )}
                            </th>
                            <th
                                className="text-left cursor-pointer"
                                onClick={() => handleSort("chargingConnectorType")}
                            >
                                {t("common.type")}
                                {getSortIcon("chargingConnectorType") && (
                                    <FontAwesomeIcon
                                        icon={getSortIcon("chargingConnectorType")}
                                        className="w-4 pl-2"
                                    />
                                )}
                            </th>
                            <th
                                className="text-left cursor-pointer"
                                onClick={() => handleSort("chargingConnectorStatus")}
                            >
                                {t("common.status")}
                                {getSortIcon("chargingConnectorStatus") && (
                                    <FontAwesomeIcon
                                        icon={getSortIcon("chargingConnectorStatus")}
                                        className="w-4 pl-2"
                                    />
                                )}
                            </th>
                            <th>
                                <div className="flex w-full justify-end items-center">
                                    <button
                                        className="block text-sm text-primary-600 hover:text-primary-400 text-left rounded-lg"
                                        onClick={() => {
                                            navigate("/connector/create", {
                                                state: {
                                                    chargingPointPublicId: pointId,
                                                },
                                            });
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} /> {t("pages.addNewConnector")}
                                    </button>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredConnectors.length > 0 ? (
                            filteredConnectors.map((connector, index) => (
                                <tr key={index}>
                                    <td className="cursor-pointer" onClick={() => {goToConnector(connector.chargingConnectorPublicId); }}>{connector.chargingConnectorPublicId || ""}</td>
                                    <td className="cursor-pointer" onClick={() => {goToConnector(connector.chargingConnectorPublicId); }}>{connector.chargingConnectorName || ""}</td>
                                    <td className="cursor-pointer" onClick={() => {goToConnector(connector.chargingConnectorPublicId); }}>{connector.power_type || ""}</td>
                                    <td className="cursor-pointer" onClick={() => {goToConnector(connector.chargingConnectorPublicId); }}><ConnectorStatusBadge status={connector.chargingConnectorStatus} />
</td>
                                    <td className="flex justify-end">
                                        <CustomDropdown
                                            isRight={true}
                                            ContentComponent={() => (
                                                <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                                    <button
                                                        className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                                        onClick={() => {goToConnector(connector.chargingConnectorPublicId); }}
                                                    >
                                                        <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                                                    </button>
                                                    <button
                                                        className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                                        onClick={() => {
                                                            setConfirmState({
                                                                isOpen: true,
                                                                title: t("confirm.deleteEntity", {
                                                                    entity: t("entity.connector"),
                                                                    name: connector.chargingConnectorName,
                                                                }),
                                                                confirm: () => {
                                                                    handleDeleteConnector(connector.chargingConnectorPublicId);
                                                                },
                                                            });
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                                    </button>
                                                </div>
                                            )}
                                        >
                                            <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                                <FontAwesomeIcon icon={faEllipsis} />
                                            </div>
                                        </CustomDropdown>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center py-4">
                                    {t("common.noEntityFound", { entity: t("entity.connectors") })}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>


            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
                onConfirm={() => {
                    setConfirmState((s) => ({ ...s, isOpen: false }));
                    confirmState.confirm();
                }}
            />
        </>
    );
};

export default PointConnectors;
